// The site's A/B tests. One runs today; `createExperiment` exists so the next
// one is an instance rather than a second copy of this plumbing.
//
// `hero_buy_first` on `/`: does leading with Buy sell more than leading with
//    the trial? The two layouts already existed but could never be compared,
//    because the buy-first one only ever rendered for a paid Google click
//    (`shoppingIntent` in TrapManLanding) and the trial-first one for everybody
//    else. Any gap between them measured the difference between traffic
//    sources, not pages. This assigns the layout at random instead.
//
//    At ~18 sessions a day on `/`: a doubling of begin_checkout is visible in
//    about 17 days, a 50% lift needs 55, and the purchase rate (1.1%) needs
//    about five months. Read it on begin_checkout, and remember that /ig
//    already shows a page can double its checkout clicks and still sell four
//    times less.
//
// `ig_no_buy` ran on `/ig` from 2026-09-04 to 2026-09-06 and was retired by
// shipping its winning arm: the Buy button is simply gone from that page. See
// IgLanding.jsx for the numbers. Events from those two days keep their
// `exp_ig_no_buy` property, so the readout still works on the history.

// Used when a store throws (private mode, storage disabled). Keyed by storage
// key, so experiments cannot read each other's fallback. Keeps the
// assignment stable for the rest of the page load rather than re-rolling on
// every read, which would show one visitor both layouts.
const fallback = new Map();

// One experiment. Everything below is the plumbing every experiment needs;
// what differs is the name, the storage key, the two arm names, and who is
// excluded.
function createExperiment({ name, storageKey, variants, isExcluded = () => false }) {
  const [controlArm, treatmentArm] = variants;

  // Our own key, not one of PostHog's. The variant has to survive the round
  // trip through Stripe so the `purchase` event can still be attributed, and
  // PostHog's own storage is wiped on every page load for EU visitors who have
  // not accepted yet (memory persistence, see analytics.js). A single letter of
  // A/B assignment identifies nobody, which is why it is written
  // unconditionally the same way `tm_evt_*` already is.
  const localStore = {
    get: () => localStorage.getItem(storageKey),
    set: (value) => localStorage.setItem(storageKey, value),
  };

  // Every read and write goes through these, so a store that throws degrades
  // the experiment instead of taking the page down with it. The catch is here
  // rather than inside localStore because the failure is the caller's problem
  // to survive whatever store it was handed.
  //
  // The fallback is consulted only when the store actually threw: falling back
  // on a merely empty answer would hand one visitor's assignment to the next.
  function read(storage) {
    try {
      return storage.get() || null;
    } catch {
      return fallback.get(storageKey) || null;
    }
  }

  function write(storage, value) {
    try {
      storage.set(value);
    } catch {
      fallback.set(storageKey, value);
    }
  }

  // Split on the half. Separate from the plumbing so the boundary is testable
  // without a DOM: everything below 0.5 is control, everything from 0.5 up is
  // the treatment.
  function assign(roll) {
    return roll < 0.5 ? controlArm : treatmentArm;
  }

  // Assigns on first call and remembers the answer. Call it only from the page
  // that actually renders one of the two arms — assigning on /docs or /login
  // would count visitors who never saw either one.
  //
  // `?exp=<arm>` forces one, for looking at both by hand. Forcing persists like
  // any other assignment, so the arm sticks while you click around; pass the
  // other value to swap back.
  function get(options = {}) {
    const { search = currentSearch(), random = Math.random, storage = localStore } = options;
    const params = new URLSearchParams(search);

    if (isExcluded(params)) return null;

    const forced = params.get("exp");
    if (variants.includes(forced)) {
      write(storage, forced);
      return forced;
    }

    const existing = read(storage);
    if (variants.includes(existing)) return existing;

    const assigned = assign(random());
    write(storage, assigned);
    return assigned;
  }

  // Reads without ever assigning. This is what analytics.js calls on every
  // event: a visitor who has only ever seen /docs is not in the experiment, and
  // asking for their variant must not put them in it.
  function peek(options = {}) {
    const { storage = localStore } = options;
    const stored = read(storage);
    return variants.includes(stored) ? stored : null;
  }

  // Registered as a PostHog super property so every later event carries the arm
  // — including `purchase`, which fires on a fresh page load coming back from
  // Stripe. Empty when the visitor is not in this experiment, so those events
  // stay unlabelled rather than being lumped into a group.
  function props(options = {}) {
    const variant = peek(options);
    return variant ? { [`exp_${name}`]: variant } : {};
  }

  return { name, assign, get, peek, props };
}

function currentSearch() {
  return typeof window === "undefined" ? "" : window.location.search;
}

// The `src` markers on our paid landing URLs: Shopping and Search.
const PAID_SRC = new Set(["gshop", "gsearch"]);

// A click we paid for.
//
// The buy-first layout and the hero experiment's exclusion both read this one
// function on purpose. If they ever disagreed, a visitor could be shown the
// buy-first hero AND enrolled in the test, and would then be counted in an arm
// whose page they never saw — a corruption with nothing visibly broken.
//
// `gclid` is Google's auto-tagging parameter, appended to every paid click.
// The `src` markers are a second tell, so the pages stay correct even if
// auto-tagging is ever switched off on the account: without them, a paid
// Search click landing on /?src=gsearch was randomised into the experiment
// and shown the trial-first hero its ad had not promised.
export function isPaidClick(search = currentSearch()) {
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(search);
  return params.has("gclid") || PAID_SRC.has(params.get("src"));
}

// ─── hero_buy_first, on `/` ───────────────────────────────────────────────
export const HERO_EXPERIMENT = "hero_buy_first";
export const CONTROL = "control";
export const BUY = "buy";
export const VARIANTS = [CONTROL, BUY];

const hero = createExperiment({
  name: HERO_EXPERIMENT,
  storageKey: "tm_exp_hero_buy_first",
  variants: VARIANTS,
  // A paid click is already forced into the buy-first hero, so rolling a dice
  // for it would file somebody under a layout they were never shown.
  isExcluded: (params) => isPaidClick(params),
});

export const assignVariant = hero.assign;
export const getHeroVariant = hero.get;
export const readVariant = hero.peek;

// The running experiments' labels on an event. One entry today; the merge and
// the null-when-empty contract are kept because analytics.js bails on null and
// would otherwise register an empty object, and because the next experiment
// should not have to reopen this function.
export function experimentProps(options = {}) {
  const merged = { ...hero.props(options) };
  return Object.keys(merged).length ? merged : null;
}
